import { Button } from 'primereact/button';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { useFormik } from 'formik';
import { InputText } from "primereact/inputtext";

import { IsNullOrEmpty } from "../../../services/CustomService";
import {
    Outlet,
    useLoaderData,
    useRouteLoaderData,
    Form,
    useLocation,
    Link,
    useRevalidator,
    useSearchParams,
    useNavigation
} from "react-router-dom";
import { Calendar } from 'primereact/calendar';
import { getTool, getTools, updateClassification, getToolCategories, getToolSubCategories, ToolStats, syncToKiwiiz, unsyncToKiwiiz, deleteTool, updateToolOptions } from "../../../data/loca/tools";
import { getExpenses, deleteExpense } from "../../../data/loca/expenses";
import { getRentalsHistory, getUpcomingRentalsForTool } from "../../../data/loca/rentals";
import { getSettings } from "../../../data/loca/settings";
import { getMerchandise } from "../../../data/loca/merchandise";
import { Chart } from 'primereact/chart';
import React, { useState, useEffect } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { Dialog } from 'primereact/dialog';
import { send, notify } from "../../../services/NotificationService";
import { Panel } from 'primereact/panel';
import { classNames } from 'primereact/utils';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { AutoComplete } from 'primereact/autocomplete';
import ToolAssetsManager from './assets-manager';
import ContractAnnexesManager from './contract-annexes-manager';
import { EquipmentGroupEditor } from './equipment-groups';
// Comparaison sans accents ni casse pour filtrer les suggestions de sous-catégorie.
const normalizeSearchText = (text) => (text ?? '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export async function loader({ params }) {
    const [tool, settings, toolCategories, toolSubCategories] = await Promise.all([
        getTool(params.toolId),
        getSettings(),
        getToolCategories(),
        getToolSubCategories().catch(() => [])
    ]);

    return { tool, settings, toolCategories, toolSubCategories };
}

export async function detailsLoader({ params }) {
    const [upcomingReservations, merchandise] = await Promise.all([
        getUpcomingRentalsForTool(params.toolId),
        getMerchandise()
    ]);

    return { upcomingReservations, merchandise };
}

// Historique : période dans l'URL (?from=AAAA-MM-JJ&to=AAAA-MM-JJ), sur la date de fin de location.
const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const toIsoDay = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const fromIsoDay = (value) => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
};
const readIsoDay = (value) => (value && ISO_DAY_PATTERN.test(value) ? value : null);

const HISTORY_PERIODS = [
    { key: 'all', label: 'Tout', range: () => ({ from: null, to: null }) },
    {
        key: '30d', label: '30 derniers jours', range: (today) => {
            const from = new Date(today);
            from.setDate(from.getDate() - 29);
            return { from: toIsoDay(from), to: toIsoDay(today) };
        }
    },
    {
        key: '12m', label: '12 derniers mois', range: (today) => {
            const from = new Date(today);
            from.setFullYear(from.getFullYear() - 1);
            from.setDate(from.getDate() + 1);
            return { from: toIsoDay(from), to: toIsoDay(today) };
        }
    },
    { key: 'year', label: 'Cette année', range: (today) => ({ from: `${today.getFullYear()}-01-01`, to: `${today.getFullYear()}-12-31` }) },
    { key: 'lastYear', label: 'Année dernière', range: (today) => ({ from: `${today.getFullYear() - 1}-01-01`, to: `${today.getFullYear() - 1}-12-31` }) }
];

const formatEuros = (cents) => ((cents ?? 0) / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: (cents ?? 0) % 100 === 0 ? 0 : 2 });
const formatMonthLabel = (month) => {
    const [year, monthIndex] = month.split('-').map(Number);
    return new Date(year, monthIndex - 1, 1).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};
const plural = (count, singular, pluralForm) => `${count.toLocaleString('fr-FR')} ${count > 1 ? pluralForm : singular}`;

function ToolHistorySummary({ summary }) {
    if (!summary) {
        return null;
    }

    const occupancy = summary.occupancyRate == null ? "—" : `${(summary.occupancyRate * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
    const daysSinceLastRental = summary.lastRentalEnd
        ? Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(summary.lastRentalEnd).setHours(0, 0, 0, 0)) / 86400000)
        : null;
    const tiles = [
        {
            label: "Locations",
            value: summary.rentalCount.toLocaleString('fr-FR'),
            detail: summary.distinctClients > 0
                ? `${plural(summary.distinctClients, "client", "clients")}, dont ${plural(summary.returningClients, "fidèle", "fidèles")}`
                : "Aucun client"
        },
        {
            label: "CA part outil",
            value: formatEuros(summary.toolAmount),
            detail: `Panier moyen ${formatEuros(summary.averageBasket)} · ${formatEuros(summary.amountPerRentedDay)}/jour loué`
        },
        {
            label: "Jours loués",
            value: summary.rentedDays.toLocaleString('fr-FR'),
            detail: `Durée moyenne ${summary.averageDurationDays.toLocaleString('fr-FR')} j`
        },
        {
            label: "Taux d'occupation",
            value: occupancy,
            detail: `Sur ${plural(summary.periodDays, "jour", "jours")}${summary.stock > 1 ? ` × ${summary.stock} en stock` : ""}`
        },
        {
            label: "Résultat net",
            value: formatEuros(summary.netResult),
            detail: `Dépenses ${formatEuros(summary.expenses)}`,
            tone: summary.netResult < 0 ? "negative" : null
        },
        {
            label: "Dernière location",
            value: summary.lastRentalEnd ? new Date(summary.lastRentalEnd).toLocaleDateString('fr-FR') : "—",
            detail: daysSinceLastRental == null ? "Aucune sur la période"
                : daysSinceLastRental <= 0 ? "Aujourd'hui" : `Il y a ${plural(daysSinceLastRental, "jour", "jours")}`
        }
    ];

    return (
        <div className="tool-history-kpis">
            {tiles.map((tile) => (
                <div key={tile.label} className={classNames("tool-history-kpi", { "tool-history-kpi--negative": tile.tone === "negative" })}>
                    <span className="tool-history-kpi__label">{tile.label}</span>
                    <strong className="tool-history-kpi__value">{tile.value}</strong>
                    <span className="tool-history-kpi__detail">{tile.detail}</span>
                </div>
            ))}
        </div>
    );
}

function ToolHistoryMonthlyChart({ months }) {
    if (!months || months.length < 2) {
        return null;
    }

    const data = {
        labels: months.map((month) => formatMonthLabel(month.month)),
        datasets: [{
            data: months.map((month) => month.toolAmount / 100),
            backgroundColor: '#005d6c',
            hoverBackgroundColor: '#00434e',
            borderRadius: 4,
            borderSkipped: 'start',
            maxBarThickness: 28
        }]
    };
    const options = {
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                displayColors: false,
                callbacks: {
                    label: (context) => {
                        const month = months[context.dataIndex];
                        return [formatEuros(month.toolAmount), plural(month.rentalCount, "location", "locations")];
                    }
                }
            }
        },
        scales: {
            x: { grid: { display: false }, ticks: { color: '#64748b', maxRotation: 0, autoSkip: true } },
            y: {
                beginAtZero: true,
                border: { display: false },
                grid: { color: '#eef1f5' },
                ticks: { color: '#64748b', callback: (value) => `${value.toLocaleString('fr-FR')} €` }
            }
        }
    };

    return (
        <div className="tool-history-chart">
            <h4>CA part outil par mois</h4>
            <div className="tool-history-chart__canvas">
                <Chart type="bar" data={data} options={options} style={{ height: '100%' }} />
            </div>
        </div>
    );
}

export async function historyLoader({ params, request }) {
    const url = new URL(request.url);
    const from = readIsoDay(url.searchParams.get('from'));
    const to = readIsoDay(url.searchParams.get('to'));
    const rentalsPage = await getRentalsHistory(params.toolId, 0, 100, from, to);

    return { rentalsPage, toolId: params.toolId, from, to };
}

export async function expensesLoader({ params }) {
    const expenses = await getExpenses(params.toolId);

    return { expenses };
}
const acceptExpense = (id) => {
    deleteExpense(id);
    window.location.reload();
};

const confirmDeleteExpense = (event, id) => {
    confirmPopup({
        target: event.currentTarget,
        message: 'Êtes-vous sûr de vouloir supprimer cette dépense ?',
        icon: 'pi pi-info-circle',
        defaultFocus: 'reject',
        acceptClassName: 'p-button-danger',
        acceptLabel: 'Oui',
        rejectLabel: 'Non',
        accept: () => { acceptExpense(id); }
    });
};

export default function ToolLayout() {
    const { tool, toolCategories, toolSubCategories, settings } = useLoaderData();
    const location = useLocation();
    const revalidator = useRevalidator();
    const [kiwiizVisible, setKiwiizVisible] = useState(false);

    const [kiwiizLink, setKiwiizLink] = useState(tool.kiwiizLink);
    const [selectedPictureIndex, setSelectedPictureIndex] = useState(0);

    const [editCategoryVisible, setEditCategoryVisible] = useState(false);
    const [isSavingCategory, setIsSavingCategory] = useState(false);
    // Matériel partagé : les annonces du loueur ne sont chargées qu'à l'ouverture de l'éditeur.
    const [equipmentEditorVisible, setEquipmentEditorVisible] = useState(false);
    const [ownerTools, setOwnerTools] = useState(null);
    const openEquipmentEditor = async () => {
        if (!ownerTools) {
            setOwnerTools((await getTools()) ?? []);
        }
        setEquipmentEditorVisible(true);
    };
    const equipmentGroup = tool.equipmentGroup;
    const otherEquipmentTools = (equipmentGroup?.tools ?? []).filter((member) => member.publicId !== tool.publicId);

    const accept = () => {
        var response = deleteTool(tool.publicId);
        // on affiche le message de reponse
        response.then((response) => {
            if (response.status === 200) {
                send("success", response.message)
                window.location.href = "/outils/liste";
            }
            else {
                send("error", response.message)
                notify();
            }
        });
    };

    const reject = () => {
    };

    const deleteToolAction = (event) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Êtes-vous sûr de vouloir supprimer cet outil ?',
            icon: 'pi pi-info-circle',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            acceptLabel: 'Oui',
            rejectLabel: 'Non',
            accept,
            reject
        });
    };
    const addLocationToTool = () => {
        let link = `/ajouter/?toolId=${tool.publicId}`
        //redirect to link
        //go
        window.location.href = link;

    };

    const formik = useFormik({
        initialValues: {
            categoryId: tool.categoryId,
            secondaryCategoryIds: tool.secondaryCategoryIds ?? [],
            subCategory: tool.subCategory ?? ''
        },
        enableReinitialize: true,
        validate: (data) => {
            let errors = {};



            return errors;
        }
    });
    const isFormFieldInvalid = (name) => !!(formik.touched[name] && formik.errors[name]);

    // Sous-catégories déjà utilisées par le loueur (plus celle de l'outil si elle n'y figure pas : outil d'un autre
    // loueur consulté en admin). Saisie libre possible : le bouton montre toute la liste, la frappe la filtre.
    const subCategoryNames = (toolSubCategories ?? []).map((option) => option.name);
    if (tool.subCategory && !subCategoryNames.includes(tool.subCategory)) {
        subCategoryNames.unshift(tool.subCategory);
    }
    const [subCategorySuggestions, setSubCategorySuggestions] = useState([]);
    const searchSubCategories = (event) => {
        const query = normalizeSearchText(event.query);
        setSubCategorySuggestions(subCategoryNames.filter((name) => !query || normalizeSearchText(name).includes(query)));
    };

    const editCategory = () => {
        var catId = formik.values.categoryId;

        setIsSavingCategory(true);
        // La catégorie principale ne peut pas aussi être secondaire.
        const secondaryCategoryIds = (formik.values.secondaryCategoryIds ?? []).filter((id) => id !== catId);
        var ret = updateClassification(tool.publicId, catId, formik.values.subCategory, secondaryCategoryIds);
        ret.then((a) => {
            if (a.status === 200) {
                send("success", "Catégorie mise à jour")
                setEditCategoryVisible(false);
                revalidator.revalidate();
            } else if (a.status === 400) {
                send("warning", a.message)
            } else {
                send("error", a.message)
            }
            notify();
        }).finally(() => {
            setIsSavingCategory(false);
        });

    };
    var link = settings.publicSiteUrl + "/outil/" + tool.customUrl;

    const currentSection = location.pathname.split("/")[3] || "details";
    const showFullHero = currentSection === "details";
    const toolPictures = [
        tool.base64Picture,
        ...((tool.pictures ?? []).map((picture) => picture.base64Picture))
    ].filter((picture) => !!picture);
    const selectedPicture = toolPictures[selectedPictureIndex] ?? toolPictures[0];

    useEffect(() => {
        setSelectedPictureIndex(0);
    }, [tool.publicId]);

    return (
        <div className="tool-details">
            <section className="tool-details-hero">
                <div className="tool-details-media">
                    {(!IsNullOrEmpty(selectedPicture)) ? (
                        <>
                            <img
                                className="rentalPicture"
                                height={showFullHero ? 192 : 140}
                                key={`${tool.publicId}-${selectedPictureIndex}`}
                                alt={tool.name}
                                src={selectedPicture || null}
                            />
                            {showFullHero && toolPictures.length > 1 && (
                                <div className="tool-details-gallery" aria-label="Photos du matériel">
                                    {toolPictures.map((picture, index) => (
                                        <button
                                            type="button"
                                            className={classNames("tool-details-gallery__thumb", { "is-active": index === selectedPictureIndex })}
                                            onClick={() => setSelectedPictureIndex(index)}
                                            key={`${picture}-${index}`}
                                            aria-label={`Afficher la photo ${index + 1}`}
                                        >
                                            <img src={picture} alt="" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="no-picture no-picture-large"></div>
                    )}
                </div>

                <div className="tool-details-info">
                    <div className="tool-details-title">
                        <div>
                            <div className="tool-details-eyebrow">
                                <span>{(tool.categories?.length ? tool.categories.join(" + ") : tool.category) || "Sans catégorie"}{tool.subCategory ? ` · ${tool.subCategory}` : ""}</span>
                                <button type="button" onClick={() => setEditCategoryVisible(true)} className="pi pi-pencil" aria-label="Modifier la catégorie"></button>
                            </div>
                            <h1>{tool.name} </h1>
                            {showFullHero && !IsNullOrEmpty(tool.smallDescription) && (
                                <p className="tool-details-subtitle">{tool.smallDescription}</p>
                            )}
                        </div>
                        {(tool.isDeleted === false) ? (
                            <div className="tool-details-actions">
                                <Form action="modification">
                                    <Button type="submit" size="small">Modifier l'outil</Button>
                                </Form>
                                <ConfirmPopup />
                                <Button onClick={deleteToolAction} icon="pi pi-trash" label="" size="small" className="p-button-danger"></Button>
                                <Button onClick={() => addLocationToTool()} icon="pi pi-plus" className="iconSteps" label="" size="small" severity="secondary"> Location</Button>
                                <a className="tool-details-link" href={link} rel="noopener noreferrer" target="_blank" >
                                    <i className="pi pi-desktop" ></i> Page publique
                                </a>
                            </div>
                        ) : (
                            <i> L'outil a été supprimé !</i>
                        )}
                    </div>

                    {(tool.isDeleted === false && showFullHero) && (
                        <>
                            <div className="tool-details-meta">
                                <div>
                                    <span>Marque</span>
                                    <strong>{tool.brand || "Non renseignée"}</strong>
                                </div>
                                <div>
                                    <span>Modèle</span>
                                    <strong>{tool.model || "Non renseigné"}</strong>
                                </div>
                                <div>
                                    <span>Sous-catégorie</span>
                                    <strong>{tool.subCategory || "Non renseignée"}</strong>
                                </div>
                                <div>
                                    <span>{equipmentGroup ? "Stock commun" : "Stock"}</span>
                                    <strong>{equipmentGroup ? equipmentGroup.stockTotal : (tool.stockTotal ?? "—")}</strong>
                                </div>
                                <div>
                                    <span>Caution</span>
                                    <strong>{tool.depositCents / 100}&euro;</strong>
                                </div>
                                <div>
                                    <span>Prix / jour</span>
                                    <strong>{tool.priceDayCents / 100}&euro;</strong>
                                </div>
                                <div>
                                    <span>2 jours</span>
                                    <strong>{tool.priceDayAfterTwoDays / 100}&euro;</strong>
                                </div>
                                <div>
                                    <span>7 jours</span>
                                    <strong>{tool.priceDayAfterSevenDays / 100}&euro;</strong>
                                </div>
                                <div>
                                    <span>Acompte</span>
                                    <strong>{tool.advancedPaymentPercentRequired ?? 0}%</strong>
                                </div>
                            </div>
                            <div className={classNames("tool-equipment-banner", { "is-shared": !!equipmentGroup })}>
                                <i className="pi pi-sitemap" aria-hidden="true"></i>
                                {equipmentGroup ? (
                                    <div>
                                        <span>Même matériel que</span>
                                        <p>
                                            {otherEquipmentTools.map((member, index) => (
                                                <React.Fragment key={member.publicId}>
                                                    {index > 0 && ", "}
                                                    <Link to={`/monoutil/${member.publicId}/details`}>{member.name}</Link>
                                                </React.Fragment>
                                            ))}
                                        </p>
                                        <small>« {equipmentGroup.displayName} » : louer l'une de ces annonces rend les autres indisponibles ; statistiques cumulées.</small>
                                    </div>
                                ) : (
                                    <div>
                                        <span>Matériel partagé</span>
                                        <small>Cette machine est aussi proposée sous une autre annonce (multi-outil…) ? Regroupez-les pour partager le stock.</small>
                                    </div>
                                )}
                                <Button type="button" size="small" text icon={equipmentGroup ? "pi pi-pencil" : "pi pi-link"}
                                    label={equipmentGroup ? "Modifier" : "Regrouper"} onClick={openEquipmentEditor} />
                            </div>
                            {(tool.keywords || tool.accessories || tool.paidOptions) && (
                                <div className="tool-details-highlights">
                                    {!IsNullOrEmpty(tool.accessories) && (
                                        <div>
                                            <span>Accessoires inclus</span>
                                            <p>{tool.accessories}</p>
                                        </div>
                                    )}
                                    {!IsNullOrEmpty(tool.paidOptions) && (
                                        <div>
                                            <span>Consommations payantes</span>
                                            <p>{tool.paidOptions}</p>
                                        </div>
                                    )}
                                    {!IsNullOrEmpty(tool.keywords) && (
                                        <div className="tool-details-tags">
                                            <span>Mots-clés</span>
                                            <div className="tool-details-tags-list">
                                                {tool.keywords
                                                    .split(",")
                                                    .map((keyword) => keyword.trim())
                                                    .filter((keyword) => keyword.length > 0)
                                                    .map((keyword) => (
                                                        <span key={keyword} className="tool-details-tag">
                                                            {keyword}
                                                        </span>
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>

            <Dialog
                visible={editCategoryVisible}
                modal
                onHide={() => { if (!editCategoryVisible) return; setEditCategoryVisible(false); }}
                content={({ hide }) => (
                    <div className="flex flex-column px-8 py-5 gap-4" style={{ borderRadius: '12px', backgroundImage: 'radial-gradient(circle at left top, var(--primary-400), var(--primary-700))' }}>

                        <div className="inline-flex flex-column gap-2">

                            <label htmlFor="paymentFeesAmount" className="text-primary-50 font-semibold">Catégorie
                            </label>
                            <Dropdown data-cy="deliveryDropD" id="categoryId" name="categoryId" value={formik.values.categoryId}
                                onChange={(e) => {
                                    formik.setFieldValue('categoryId', e.value);
                                }}
                                className={"w-full md:w-14rem" && classNames({ 'p-invalid': isFormFieldInvalid('categoryId') })}
                                options={toolCategories} optionLabel="name" optionValue="value" placeholder="S&eacute;lectionnez une catégorie"
                            />
                        </div>

                        <div className="inline-flex flex-column gap-2">

                            <label htmlFor="secondaryCategoryIds" className="text-primary-50 font-semibold">Catégories secondaires
                            </label>
                            <MultiSelect id="secondaryCategoryIds" name="secondaryCategoryIds"
                                value={(formik.values.secondaryCategoryIds ?? []).filter((id) => id !== formik.values.categoryId)}
                                onChange={(e) => {
                                    formik.setFieldValue('secondaryCategoryIds', e.value);
                                }}
                                options={toolCategories.filter((category) => category.value !== formik.values.categoryId)}
                                optionLabel="name" optionValue="value" display="chip" placeholder="Aucune"
                                className="w-full md:w-14rem"
                            />
                            <small className="text-primary-100">Le matériel est aussi listé dans ces catégories sur lokapp.fr (et sur lokapp.events pour « Évènementiel »).</small>

                        </div>

                        <div className="inline-flex flex-column gap-2">

                            <label htmlFor="subCategory" className="text-primary-50 font-semibold">Sous-catégorie
                            </label>
                            <AutoComplete inputId="subCategory" value={formik.values.subCategory ?? ''}
                                suggestions={subCategorySuggestions} completeMethod={searchSubCategories} dropdown
                                onChange={(e) => {
                                    formik.setFieldValue('subCategory', e.value ?? '');
                                }}
                                placeholder="Aucune, ou saisissez-en une nouvelle"
                                className={classNames("w-full md:w-14rem", { 'p-invalid': isFormFieldInvalid('subCategory') })}
                                inputClassName="w-full"
                            />

                        </div>

                        <div className="flex align-items-center gap-2">
                            <Button label="Modifier" onClick={editCategory} loading={isSavingCategory} disabled={isSavingCategory} text className="p-3 w-full text-primary-50 border-1 border-white-alpha-30 hover:bg-white-alpha-10"></Button>
                            <Button label="Annuler" onClick={(e) => hide(e)} disabled={isSavingCategory} text className="p-3 w-full text-primary-50 border-1 border-white-alpha-30 hover:bg-white-alpha-10"></Button>
                        </div>
                    </div>
                )}
            ></Dialog>

            <EquipmentGroupEditor
                visible={equipmentEditorVisible}
                onHide={() => setEquipmentEditorVisible(false)}
                group={equipmentGroup}
                initialToolPublicIds={[tool.publicId]}
                tools={ownerTools ?? []}
                onSaved={() => { setOwnerTools(null); revalidator.revalidate(); }}
            />

            <Outlet />
    </div>
    );
}

export function ToolDetailsPage() {
    const { tool } = useRouteLoaderData("tool-detail") ?? {};
    const { upcomingReservations = [], merchandise = [] } = useLoaderData() ?? {};

    return (
        <section className="tool-details-content">
            <div className="tool-details-card tool-details-card-full">
                <h2>Description</h2>
                <p className="tool-details-description">{tool.description || "Aucune description renseignée."}</p>
            </div>
            <ToolOptionsManager tool={tool} merchandise={merchandise} />
            <ToolAvailabilityCalendar tool={tool} reservations={upcomingReservations} />
            <div className="tool-details-card tool-details-contract">
                <h2>Données contrat</h2>
                <div className="tool-details-contract-grid">
                    <div>
                        <h3>Utilisation</h3>
                        <p>{tool.specificitiesUseDescription || "Pas de consignes spécifiques pour le moment."}</p>
                    </div>
                    <div>
                        <h3>Retour du matériel</h3>
                        <p>{tool.materialReturnClause || "Conditions de retour non précisées."}</p>
                    </div>
                    <div>
                        <h3>Consommations payantes</h3>
                        <p>{tool.paidOptions || "Aucune consommation payante indiquée."}</p>
                    </div>
                </div>
            </div>
            <div className="tool-details-card">
                <h2>Livraison</h2>
                <p>{tool.deliveryMemo || "Aucune note de livraison."}</p>
            </div>
            <div className="tool-details-card">
                <h2>Également disponible</h2>
                <p>{tool.alsoAvailableInformation || "Aucun texte pour [EGALEMENT_DISPONIBLE] dans le message AlloVoisins."}</p>
            </div>
        </section>
    );
}

function ToolOptionsManager({ tool, merchandise = [] }) {
    const formatOption = (option) => ({
        Id: option.id ?? option.Id,
        Name: option.name ?? option.Name ?? "",
        Details: option.details ?? option.Details ?? "",
        PriceCents: option.priceCents ?? option.PriceCents ?? 0,
        MerchandiseItemId: option.merchandiseItemId ?? option.MerchandiseItemId ?? null
    });

    const [options, setOptions] = useState((tool.options || []).map(formatOption));
    const [editingOptionId, setEditingOptionId] = useState(null);
    const [isSavingOptions, setIsSavingOptions] = useState(false);
    const [optionForm, setOptionForm] = useState({
        name: "",
        details: "",
        price: "",
        merchandiseItemId: null
    });

    useEffect(() => {
        setOptions((tool.options || []).map(formatOption));
    }, [tool]);

    const merchandiseOptions = [
        { name: "Aucune marchandise liée", value: null },
        ...merchandise
            .slice()
            .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "fr-FR", { sensitivity: "base" }))
            .map((item) => ({ name: `${item.name}${item.quantityInStock != null ? ` (${item.quantityInStock} en stock)` : ""}`, value: item.id }))
    ];

    const resetOptionForm = () => {
        setEditingOptionId(null);
        setOptionForm({
            name: "",
            details: "",
            price: "",
            merchandiseItemId: null
        });
    };

    const onOptionFormChange = (field, value) => {
        setOptionForm((previous) => ({
            ...previous,
            [field]: value
        }));
    };

    const onMerchandiseChange = (merchandiseItemId) => {
        onOptionFormChange("merchandiseItemId", merchandiseItemId ?? null);

        if (merchandiseItemId == null) {
            return;
        }

        const selectedMerchandise = merchandise.find((item) => item.id === merchandiseItemId);
        if (!selectedMerchandise) {
            return;
        }

        setOptionForm((previous) => ({
            ...previous,
            name: selectedMerchandise.name ?? "",
            details: selectedMerchandise.name ?? "",
            price: selectedMerchandise.suggestedRetailPrice != null ? selectedMerchandise.suggestedRetailPrice.toString() : previous.price,
            merchandiseItemId
        }));
    };

    const saveOptionInList = () => {
        const optionName = optionForm.name?.trim();
        const optionDetails = optionForm.details?.trim() ?? "";
        const optionPrice = Number(optionForm.price);
        const merchandiseItemId = optionForm.merchandiseItemId ? Number(optionForm.merchandiseItemId) : null;

        if (!optionName) {
            send("warning", "Veuillez renseigner l'intitulé de l'option.");
            notify();
            return;
        }

        if (!optionPrice || optionPrice <= 0 || Number.isNaN(optionPrice)) {
            send("warning", "Veuillez renseigner un prix valide pour l'option.");
            notify();
            return;
        }

        const nextOption = {
            Id: editingOptionId ?? Math.min(0, ...options.map((option) => option.Id)) - 1,
            Name: optionName,
            Details: optionDetails,
            PriceCents: Math.round(optionPrice * 100),
            MerchandiseItemId: Number.isNaN(merchandiseItemId) ? null : merchandiseItemId
        };

        if (editingOptionId !== null) {
            setOptions((previousOptions) => previousOptions.map((option) => option.Id === editingOptionId ? nextOption : option));
        } else {
            setOptions((previousOptions) => [...previousOptions, nextOption]);
        }

        resetOptionForm();
    };

    const editOption = (option) => {
        setEditingOptionId(option.Id);
        setOptionForm({
            name: option.Name ?? "",
            details: option.Details ?? "",
            price: option.PriceCents ? (option.PriceCents / 100).toString() : "",
            merchandiseItemId: option.MerchandiseItemId ?? null
        });
    };

    const deleteOption = (optionId) => {
        setOptions((previousOptions) => previousOptions.filter((option) => option.Id !== optionId));
        if (editingOptionId === optionId) {
            resetOptionForm();
        }
    };

    const saveOptions = async () => {
        setIsSavingOptions(true);
        try {
            const response = await updateToolOptions(tool.publicId, options);
            if (response?.status === 200) {
                send("success", response.message || "Options mises à jour");
            } else {
                send("warning", response?.message || "Les options n'ont pas pu être mises à jour.");
            }
        } catch (error) {
            send("error", "Erreur lors de la mise à jour des options.");
        } finally {
            setIsSavingOptions(false);
            notify();
        }
    };

    return (
        <div className="tool-details-card tool-details-card-full">
            <div className="tool-availability__header">
                <div>
                    <h2>Options payantes</h2>
                    <p>Ajoutez ou modifiez les options disponibles à la location pour cet outil.</p>
                </div>
                <Button type="button" size="small" icon="pi pi-save" label="Enregistrer" onClick={saveOptions} loading={isSavingOptions} />
            </div>

            <div className="contentForm">
                <span className="p-float-label">
                    <InputText id="tool-option-name" value={optionForm.name} onChange={(e) => onOptionFormChange("name", e.target.value)} />
                    <label htmlFor="tool-option-name">Intitulé de l'option</label>
                </span>

                <span className="p-float-label">
                    <InputText id="tool-option-details" value={optionForm.details} onChange={(e) => onOptionFormChange("details", e.target.value)} />
                    <label htmlFor="tool-option-details">Détails</label>
                </span>

                <span className="p-float-label">
                    <InputText id="tool-option-price" value={optionForm.price} keyfilter="money" onChange={(e) => onOptionFormChange("price", e.target.value)} />
                    <label htmlFor="tool-option-price">Prix</label>
                </span>

                <span className="p-float-label">
                    <Dropdown
                        id="tool-option-merchandise"
                        value={optionForm.merchandiseItemId}
                        options={merchandiseOptions}
                        optionLabel="name"
                        optionValue="value"
                        showClear
                        filter
                        onChange={(e) => onMerchandiseChange(e.value)}
                    />
                    <label htmlFor="tool-option-merchandise">Marchandise liée</label>
                </span>

                <Button type="button" size="small" icon={editingOptionId !== null ? "pi pi-check" : "pi pi-plus"} label={editingOptionId !== null ? "Modifier" : undefined} onClick={saveOptionInList} />
                {editingOptionId !== null && (
                    <Button type="button" size="small" icon="pi pi-times" text severity="secondary" onClick={resetOptionForm} />
                )}
            </div>

            {options.length === 0 && (
                <p>Aucune option payante pour le moment.</p>
            )}
            {options.length > 0 && (
                <table className="historyTable">
                    <thead>
                        <tr>
                            <th>Intitulé</th>
                            <th>Détails</th>
                            <th>Prix</th>
                            <th>Marchandise liée</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {options.map((option) => (
                            <tr key={option.Id}>
                                <td>{option.Name}</td>
                                <td>{option.Details}</td>
                                <td>{option.PriceCents / 100}&euro;</td>
                                <td>{merchandise.find((item) => item.id === option.MerchandiseItemId)?.name || "—"}</td>
                                <td>
                                    <Button type="button" onClick={() => editOption(option)} icon="pi pi-pencil" size="small" rounded text aria-label="Modifier" />
                                    <Button type="button" onClick={() => deleteOption(option.Id)} icon="pi pi-times" size="small" rounded text severity="danger" aria-label="Supprimer" />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function ToolAvailabilityCalendar({ tool, reservations = [] }) {
    // Matériel partagé : stock commun et réservations de toutes ses annonces.
    const equipmentGroup = tool?.equipmentGroup;
    const stockTotal = Math.max(1, Number(equipmentGroup?.stockTotal ?? tool?.stockTotal ?? 1));
    const today = startOfDay(new Date());
    const months = buildCalendarMonths(reservations, today);
    const reservationLookup = buildReservationLookup(reservations);
    const attentionLookup = buildReservationAttentionLookup(reservations);
    const nextReservations = reservations
        .slice()
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

    return (
        <div className="tool-details-card tool-availability tool-details-card-full">
            <div className="tool-availability__header">
                <div>
                    <h2>Calendrier des réservations à venir</h2>
                    <p>
                        {equipmentGroup ? `Stock commun du matériel « ${equipmentGroup.displayName} » : ${stockTotal}` : `Stock total : ${stockTotal}`}.
                        {" "}Les jours marqués complets n'ont plus d'exemplaire disponible.
                        {equipmentGroup && " Les réservations des autres annonces de ce matériel sont comprises."}
                    </p>
                </div>
                <div className="tool-availability__legend" aria-label="Légende disponibilité">
                    <span><i className="tool-availability__dot tool-availability__dot--free"></i> Libre</span>
                    <span><i className="tool-availability__dot tool-availability__dot--partial"></i> Partiel</span>
                    <span><i className="tool-availability__dot tool-availability__dot--full"></i> Complet</span>
                    <span><i className="tool-availability__hatch"></i> À sécuriser</span>
                </div>
            </div>

            <div className="tool-availability__months">
                {months.map((month) => (
                    <div className="tool-availability__month" key={month.key}>
                        <h3>{month.label}</h3>
                        <div className="tool-availability__weekdays" aria-hidden="true">
                            {["L", "M", "M", "J", "V", "S", "D"].map((day, index) => (
                                <span key={`${day}-${index}`}>{day}</span>
                            ))}
                        </div>
                        <div className="tool-availability__grid">
                            {month.days.map((day, dayIndex) => {
                                if (!day) {
                                    return <span className="tool-availability__empty" key={`empty-${month.key}-${dayIndex}`}></span>;
                                }

                                const dateKey = toDateKey(day);
                                const reserved = reservationLookup.get(dateKey) ?? 0;
                                const requiresAttention = attentionLookup.has(dateKey);
                                const remaining = Math.max(0, stockTotal - reserved);
                                const isPast = day < today;
                                const state = reserved <= 0 ? "free" : remaining > 0 ? "partial" : "full";
                                const title = reserved <= 0
                                    ? "Aucune réservation"
                                    : `${reserved}/${stockTotal} exemplaire(s) réservé(s)${requiresAttention ? " - signature ou acompte manquant" : ""}`;

                                return (
                                    <span
                                        className={`tool-availability__day tool-availability__day--${state}${requiresAttention ? " tool-availability__day--attention" : ""}${isPast ? " tool-availability__day--past" : ""}${dateKey === toDateKey(today) ? " tool-availability__day--today" : ""}`}
                                        title={title}
                                        key={dateKey}
                                    >
                                        <strong>{day.getDate()}</strong>
                                        <small>{remaining}/{stockTotal}</small>
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="tool-availability__list">
                <h3>Réservations à venir</h3>
                {nextReservations.length === 0 && (
                    <p>Aucune réservation à venir pour cet outil.</p>
                )}
                {nextReservations.length > 0 && (
                    <ul>
                        {nextReservations.map((reservation) => {
                            const rentalId = reservation.rentalPublicId ?? reservation.publicId ?? reservation.id;
                            const reservationContent = (
                                <>
                                    <span>
                                        {formatDateRange(reservation.startDate, reservation.endDate)}
                                    </span>
                                    <strong>
                                        {reservation.quantity ?? 1} exemplaire{(reservation.quantity ?? 1) > 1 ? "s" : ""}
                                    </strong>
                                    <em>{reservation.clientFirstname} {reservation.clientLastname}</em>
                                    {reservation.toolNames && (
                                        <small className="tool-availability__tools">{reservation.toolNames}</small>
                                    )}
                                    {reservation.requiresAttention && (
                                        <small className="tool-availability__warning">
                                            {[
                                                reservation.missingElectronicSignature ? "signature manquante" : null,
                                                reservation.missingAdvancePayment ? "acompte manquant" : null
                                            ].filter(Boolean).join(" + ")}
                                        </small>
                                    )}
                                </>
                            );

                            return (
                                <li key={rentalId ?? `${reservation.startDate}-${reservation.endDate}`}>
                                    {rentalId ? (
                                        <Link className="tool-availability__reservation-link" to={`/${rentalId}`}>
                                            {reservationContent}
                                        </Link>
                                    ) : (
                                        <span className="tool-availability__reservation-link tool-availability__reservation-link--disabled">
                                            {reservationContent}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}

function buildCalendarMonths(reservations, today) {
    const latestReservationEnd = reservations.reduce((latest, reservation) => {
        const end = new Date(reservation.endDate);
        return end > latest ? end : latest;
    }, addMonths(today, 1));

    const months = [];
    let cursor = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastMonth = new Date(latestReservationEnd.getFullYear(), latestReservationEnd.getMonth(), 1);

    while (cursor <= lastMonth) {
        months.push(buildMonth(cursor));
        cursor = addMonths(cursor, 1);
    }

    return months;
}

function buildMonth(monthDate) {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const leadingEmptyDays = (firstDay.getDay() + 6) % 7;
    const days = Array.from({ length: leadingEmptyDays }, () => null);

    for (let day = 1; day <= lastDay.getDate(); day++) {
        days.push(new Date(year, month, day));
    }

    return {
        key: `${year}-${month}`,
        label: firstDay.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
        days
    };
}

function buildReservationLookup(reservations) {
    const lookup = new Map();

    reservations.forEach((reservation) => {
        const quantity = Math.max(1, Number(reservation.quantity ?? 1));
        const start = new Date(reservation.startDate);
        const end = new Date(reservation.endDate);
        start.setHours(start.getHours() - 1);
        end.setHours(end.getHours() + 1);

        let cursor = startOfDay(start);
        const lastDay = startOfDay(end);
        while (cursor <= lastDay) {
            const key = toDateKey(cursor);
            lookup.set(key, (lookup.get(key) ?? 0) + quantity);
            cursor = addDays(cursor, 1);
        }
    });

    return lookup;
}

function buildReservationAttentionLookup(reservations) {
    const lookup = new Set();

    reservations
        .filter((reservation) => reservation.requiresAttention)
        .forEach((reservation) => {
            const start = new Date(reservation.startDate);
            const end = new Date(reservation.endDate);
            start.setHours(start.getHours() - 1);
            end.setHours(end.getHours() + 1);

            let cursor = startOfDay(start);
            const lastDay = startOfDay(end);
            while (cursor <= lastDay) {
                lookup.add(toDateKey(cursor));
                cursor = addDays(cursor, 1);
            }
        });

    return lookup;
}

function startOfDay(value) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function addDays(value, days) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate() + days);
}

function addMonths(value, months) {
    return new Date(value.getFullYear(), value.getMonth() + months, 1);
}

function toDateKey(value) {
    const month = `${value.getMonth() + 1}`.padStart(2, "0");
    const day = `${value.getDate()}`.padStart(2, "0");
    return `${value.getFullYear()}-${month}-${day}`;
}

function formatDateRange(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dateFormatter = new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
    const timeFormatter = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" });

    return `${dateFormatter.format(start)} ${timeFormatter.format(start)} - ${dateFormatter.format(end)} ${timeFormatter.format(end)}`;
}

export function ToolAssetsPage() {
    const { tool, settings } = useRouteLoaderData("tool-detail") ?? {};

    return (
        <section className="tool-details-content">
            <ToolAssetsManager tool={tool} settings={settings} />
        </section>
    );
}

export function ToolContractAnnexesPage() {
    const { tool } = useRouteLoaderData("tool-detail") ?? {};
    return <section className="tool-details-content"><ContractAnnexesManager tool={tool} /></section>;
}

export function ToolHistoryPage() {
    const { rentalsPage = { items: [], totalCount: 0 }, toolId, from = null, to = null } = useLoaderData() ?? {};
    const { tool } = useRouteLoaderData("tool-detail") ?? {};
    const [, setSearchParams] = useSearchParams();
    const navigation = useNavigation();
    const equipmentGroup = tool?.equipmentGroup;
    const [rentals, setRentals] = useState(rentalsPage.items ?? []);
    const [totalRentalsCount, setTotalRentalsCount] = useState(rentalsPage.totalCount ?? rentalsPage.items?.length ?? 0);
    const [isLoadingMoreRentals, setIsLoadingMoreRentals] = useState(false);
    const [toolStats, setToolStats] = useState([]);
    const [isLoadingToolStats, setIsLoadingToolStats] = useState(false);
    const [calendarDates, setCalendarDates] = useState(null);

    let [chartDataTool, setChartDataTool] = useState({});
    let [chartOptionsTool, setChartOptionsTool] = useState({});

    const isFiltered = Boolean(from || to);
    const isChangingPeriod = navigation.state === "loading";
    const today = new Date();
    const activePeriodKey = HISTORY_PERIODS.find((period) => {
        const range = period.range(today);
        return range.from === from && range.to === to;
    })?.key ?? 'custom';

    useEffect(() => {
        setRentals(rentalsPage.items ?? []);
        setTotalRentalsCount(rentalsPage.totalCount ?? rentalsPage.items?.length ?? 0);
    }, [rentalsPage]);

    useEffect(() => {
        setCalendarDates(from || to ? [from ? fromIsoDay(from) : null, to ? fromIsoDay(to) : null] : null);
    }, [from, to]);

    const applyPeriod = (range) => {
        const nextParams = new URLSearchParams();
        if (range.from) nextParams.set('from', range.from);
        if (range.to) nextParams.set('to', range.to);
        setSearchParams(nextParams, { replace: true });
    };

    const onCalendarChange = (value) => {
        setCalendarDates(value);
        if (!value) {
            applyPeriod({ from: null, to: null });
            return;
        }
        // On attend la deuxième date ; une seule date choisie = cette journée.
        const [start, end] = value;
        if (start && end) {
            applyPeriod({ from: toIsoDay(start), to: toIsoDay(end) });
        }
    };

    const onCalendarHide = () => {
        const [start, end] = calendarDates ?? [];
        if (start && !end) {
            applyPeriod({ from: toIsoDay(start), to: toIsoDay(start) });
        }
    };

    const loadMoreRentals = async () => {
        if (!toolId || isLoadingMoreRentals) {
            return;
        }

        setIsLoadingMoreRentals(true);
        try {
            const nextPage = await getRentalsHistory(toolId, rentals.length, 100, from, to);
            setRentals((currentRentals) => [...currentRentals, ...(nextPage.items ?? [])]);
            setTotalRentalsCount(nextPage.totalCount ?? totalRentalsCount);
        } finally {
            setIsLoadingMoreRentals(false);
        }
    };

    useEffect(() => {
        if (!toolId) {
            setToolStats([]);
            return;
        }

        let isMounted = true;
        setIsLoadingToolStats(true);
        ToolStats(toolId, from, to)
            .then((stats) => {
                if (isMounted) {
                    setToolStats(stats ?? []);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setToolStats([]);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoadingToolStats(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [toolId, from, to]);

    useEffect(() => {
        var chartServicesLabels = [];
        for (let i = 0; i < toolStats.length; i++) {
            chartServicesLabels.push(toolStats[i].item1);
        }
        var chartToolValues = [];
        for (let i = 0; i < toolStats.length; i++) {
            chartToolValues.push(toolStats[i].item2);

        }
        var toolColors = [
            'rgba(158, 235, 129, 0.5)',
            'rgba(235, 163, 129, 0.5)',
            'rgba(129, 133, 235, 0.5)',
            'rgba(51, 34, 26, 0.5)',

            'rgba(53,54, 82, 0.5)',
            'rgba(88,107, 80, 0.5)',
        ];

        const servicesData = {
            labels: chartServicesLabels,
            datasets: [
                {
                    data: chartToolValues,
                    backgroundColor: toolColors,
                    hoverBackgroundColor: toolColors
                }
            ]
        };
        const toolOptions = {
            plugins: {
                legend: {
                    labels: {
                        usePointStyle: true
                    }
                }
            }
        };
        setChartDataTool(servicesData);
        setChartOptionsTool(toolOptions);
    }, [toolStats]);

    const footerHisto = (options) => {
        const className = `p-panel-footer flex flex-wrap align-items-center justify-content-between gap-3`;
        // Total calculé par l'API sur toute la période ; repli sur les lignes chargées (ancienne API).
        const toolTotalAmount = rentalsPage.totalToolAmount
            ?? rentals.reduce((acc, rental) => acc + (rental.toolAmount ?? rental.totalAmount ?? 0), 0);

        return (
            <div className={className}>
                <p><b>Total{isFiltered ? " sur la période" : ""} :</b> {totalRentalsCount}</p>
                <p><b>CA chargé :</b> {toolTotalAmount / 100}&euro;</p>
            </div>
        );
    };

    const historyFilter = (
        <div className="tool-history-filter">
            <div className="tool-history-filter__presets" role="group" aria-label="Période">
                {HISTORY_PERIODS.map((period) => (
                    <Button
                        key={period.key}
                        type="button"
                        size="small"
                        label={period.label}
                        outlined={activePeriodKey !== period.key}
                        aria-pressed={activePeriodKey === period.key}
                        onClick={() => applyPeriod(period.range(new Date()))}
                    />
                ))}
            </div>
            <Calendar
                className="tool-history-filter__range"
                value={calendarDates}
                onChange={(e) => onCalendarChange(e.value)}
                onHide={onCalendarHide}
                selectionMode="range"
                dateFormat="dd/mm/yy"
                placeholder="Période personnalisée"
                readOnlyInput
                hideOnRangeSelection
                showIcon
                aria-label="Période personnalisée"
            />
            {isChangingPeriod && <i className="pi pi-spin pi-spinner" aria-label="Chargement"></i>}
        </div>
    );

    return (
        <div id="mainContent">
            <div className="contentForm"></div >
            <Panel className="tool-details-panel" header="Historique" footerTemplate={(rentals) => footerHisto(rentals)}>
                {equipmentGroup && (
                    <p className="tool-equipment-note">
                        <i className="pi pi-sitemap" aria-hidden="true"></i> Historique et statistiques cumulés du matériel « {equipmentGroup.displayName} » :
                        {" "}{equipmentGroup.tools.map((member) => member.name).join(", ")}.
                    </p>
                )}
                {historyFilter}
                <ToolHistorySummary summary={rentalsPage.summary} />
                <ToolHistoryMonthlyChart months={rentalsPage.summary?.months} />
                <div className="contentToolsStats">
                    {rentals.length === 0 && (
                        <i>{isFiltered ? "Aucune location terminée sur cette période" : "Aucune location pour cet outil"}</i>
                    )}
                    {rentals.length > 0 && (
                        <>
                            <div className="statsCols" >
                                <table className="historyTable">
                                    <thead>
                                        <tr>
                                            <th>Client</th>
                                            {equipmentGroup && <th>Annonce</th>}
                                            <th>Début</th>
                                            <th>Fin</th>
                                            <th>Part outil</th>
                                            <th>Total location</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rentals.map((rental) => (
                                            <tr key={rental.rentalPublicId}>
                                                <td>
                                                    {rental.clientFirstname} {rental.clientLastname}
                                                </td>
                                                {equipmentGroup && <td>{rental.toolName ?? "—"}</td>}
                                                <td>{new Date(rental.startDate).toLocaleDateString()}</td>
                                                <td>{new Date(rental.endDate).toLocaleDateString()}</td>
                                                <td>{(rental.toolAmount ?? rental.totalAmount ?? 0) / 100}&euro;</td>
                                                <td>{(rental.totalAmount ?? 0) / 100}&euro;</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {rentals.length < totalRentalsCount && (
                                    <div className="contentForm">
                                        <Button
                                            type="button"
                                            size="small"
                                            severity="secondary"
                                            loading={isLoadingMoreRentals}
                                            disabled={isLoadingMoreRentals}
                                            onClick={loadMoreRentals}
                                        >
                                            Afficher plus ({rentals.length}/{totalRentalsCount})
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <div className="statsCols">
                                {isLoadingToolStats && (
                                    <i>Chargement des statistiques...</i>
                                )}
                                {!isLoadingToolStats && toolStats.length === 0 && (
                                    <i>Aucune statistique pour cette période !</i>
                                )}
                                {toolStats.length > 0 && (
                                    <div className="card">
                                        <h4 className="tool-history-chart__title">CA part outil par service</h4>
                                        <Chart type="pie" data={chartDataTool} options={chartOptionsTool} />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Panel>
            <div className="contentForm"></div >
        </div>
    );
}

export function ToolExpensesPage() {
    const { expenses = { expenses: [] } } = useLoaderData() ?? {};

    const headerExp = (options) => {
        const className = `${options.className} justify-content-space-between`;

        return (
            <div className={className}>
                <span className="font-bold">Dépenses</span>
                <Form action="expense/add">
                    <Button type="submit" size="small" icon="pi pi-plus"></Button>
                </Form>
            </div>
        );
    };

    return (
        <div id="mainContent">
            <div className="contentForm"></div >
            <Panel className="tool-details-panel" headerTemplate={(e) => headerExp(e)}>
                <Outlet />
                <div className="contentToolsStats">
                    {expenses.expenses.length === 0 && (
                        <i>Aucune dépense pour le moment</i>
                    )}
                    {expenses.expenses.length > 0 && (
                        <table className="historyTable">
                            <thead>
                                <tr>
                                    <th>Montant</th>
                                    <th>Date</th>
                                    <th>Description</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.expenses.map((expense) => (
                                    <tr key={expense.publicId}>
                                        <td>{expense.totalPrice}&euro;</td>
                                        <td>{new Date(expense.createdAt).toLocaleDateString()}</td>
                                        <td>{expense.description}</td>
                                        <td>
                                            <Button onClick={(e) => confirmDeleteExpense(e, expense.publicId)} icon="pi pi-times" size="small" rounded text severity="danger" aria-label="Cancel" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                    }
                </div>
            </Panel>
            <div className="contentForm"></div >
        </div>
    );
}
